/**
 * Generator registry — single source of truth for every generator the
 * add menu and sidebar panel can show.
 *
 * Registration is additive: adding a new generator is a single import +
 * one entry in `GENERATORS`. No other subsystem needs to know the id.
 *
 * The store's `addGenerator(id)` action and the sidebar's param editor
 * both resolve generators through `getGenerator(id)`.
 */

import { ARMCHAIR_GENERATOR } from "./armchair.ts";
import { BALL_GENERATOR } from "./ball.ts";
import { BASEBALL_BAT_GENERATOR } from "./baseball_bat.ts";
import { BED_GENERATOR } from "./bed.ts";
import { BENCH_GENERATOR } from "./bench.ts";
import { BOOKSHELF_GENERATOR } from "./bookshelf.ts";
import { BOWL_GENERATOR } from "./bowl.ts";
import { CABLE_GENERATOR } from "./cable.ts";
import { CHAIR_GENERATOR } from "./chair.ts";
import { CHEST_OF_DRAWERS_GENERATOR } from "./chest_of_drawers.ts";
import { COUCH_GENERATOR } from "./couch.ts";
import { DESK_GENERATOR } from "./desk.ts";
import { HAT_GENERATOR } from "./hat.ts";
import { LADDER_GENERATOR } from "./ladder.ts";
import { LAMP_GENERATOR } from "./lamp.ts";
import { OFFICE_CHAIR_GENERATOR } from "./office_chair.ts";
import {
  CIRCLE_GENERATOR,
  CONE_GENERATOR,
  CUBE_GENERATOR,
  CYLINDER_GENERATOR,
  ICOSPHERE_GENERATOR,
  PLANE_GENERATOR,
  SPHERE_GENERATOR,
  TORUS_GENERATOR,
} from "./primitives.ts";
import { ROOM_GENERATOR } from "./room.ts";
import { STAIRS_GENERATOR } from "./stairs.ts";
import { STOOL_GENERATOR } from "./stool.ts";
import {
  SURFACE_BOX_GENERATOR,
  SURFACE_CYLINDER_GENERATOR,
  SURFACE_INSET_GENERATOR,
  SURFACE_POLY_GENERATOR,
} from "./surface.ts";
import { TABLE_GENERATOR } from "./table.ts";
import { TEXT_GENERATOR } from "./text.ts";
import type { Generator } from "./types.ts";

export const GENERATORS: Readonly<Record<string, Generator>> = {
  cube: CUBE_GENERATOR,
  plane: PLANE_GENERATOR,
  circle: CIRCLE_GENERATOR,
  sphere: SPHERE_GENERATOR,
  icosphere: ICOSPHERE_GENERATOR,
  cylinder: CYLINDER_GENERATOR,
  cone: CONE_GENERATOR,
  torus: TORUS_GENERATOR,
  surface_box: SURFACE_BOX_GENERATOR,
  surface_cylinder: SURFACE_CYLINDER_GENERATOR,
  surface_poly: SURFACE_POLY_GENERATOR,
  surface_inset: SURFACE_INSET_GENERATOR,
  room: ROOM_GENERATOR,
  chair: CHAIR_GENERATOR,
  cable: CABLE_GENERATOR,
  stairs: STAIRS_GENERATOR,
  table: TABLE_GENERATOR,
  bookshelf: BOOKSHELF_GENERATOR,
  lamp: LAMP_GENERATOR,
  bed: BED_GENERATOR,
  ball: BALL_GENERATOR,
  bench: BENCH_GENERATOR,
  stool: STOOL_GENERATOR,
  desk: DESK_GENERATOR,
  ladder: LADDER_GENERATOR,
  armchair: ARMCHAIR_GENERATOR,
  couch: COUCH_GENERATOR,
  office_chair: OFFICE_CHAIR_GENERATOR,
  bowl: BOWL_GENERATOR,
  chest_of_drawers: CHEST_OF_DRAWERS_GENERATOR,
  hat: HAT_GENERATOR,
  baseball_bat: BASEBALL_BAT_GENERATOR,
  text: TEXT_GENERATOR,
};

export function getGenerator(id: string): Generator | undefined {
  return GENERATORS[id];
}

/** Returns true if `id` resolves to a known generator. */
export function isKnownGeneratorId(id: string): boolean {
  return id in GENERATORS;
}
